export const STORAGE_KEY = 'cvc/lastSelectedModel';
export const MODEL_PREFIX = '/cvc/model/';
export const DEFAULT_MODEL = 'nansy-v2.5';
type NansyV2 = 'nansy-v2';
type NansyV2Half = 'nansy-v2.5';
type NansyV27 = 'nansy-v2.7';
type NansyV28 = 'nansy-v2.8';
type NansyVDNet = 'nansy-v2.8-vdnet';
type NansyFineTune = `${NansyV2 | NansyV2Half | NansyV27 | NansyV28}-${string}`;
type NansyHarmonicToken = 'nansy-harmonic-token';
type NansyHarmonicTokenInterpolation = `${NansyHarmonicToken}-interpolation`;
export type ModelType =
  | NansyV2
  | NansyV2Half
  | NansyV27
  | NansyV28
  | NansyVDNet
  | NansyFineTune
  | 'speaker-control'
  | NansyHarmonicToken
  | NansyHarmonicTokenInterpolation;
// Display-label overrides for models whose derived label (title-cased identifier)
// is not what the dropdown should show. Empty today; kept so a future rename
// needs no component change.
export const MODEL_LABEL: Record<string, string> = {};
// Capability descriptions rendered as the dropdown tooltip (ModelSelect) and
// the help panel. Keys mirror `modelParamMap` exactly: every selectable mode
// has a description, and nothing else ships in the bundle (ISSUE-025 removed
// the customer-specific fine-tune entries — model identifiers themselves are
// the wire contract with the inference server and never change here).
export const MODEL_DESCRIPTIONS: { [key in ModelType]: string } = {
  'nansy-v2': 'Second-generation voice conversion model.',
  'nansy-v2-interpolation':
    'Second-generation model mode that blends multiple target voices during voice conversion.',
  'nansy-v2.5': 'Nansy v2.5 voice conversion model.',
  'nansy-v2.5-interpolation':
    'Nansy v2.5 mode that blends multiple target voices during voice conversion.',
  'nansy-v2.7': 'Nansy v2.7 voice conversion model.',
  'nansy-v2.7-interpolation':
    'Nansy v2.7 mode that blends multiple target voices during voice conversion.',
  'nansy-v2.8': 'Latest-generation voice conversion model.',
  'nansy-v2.8-vdnet':
    'VDNet variant of nansy-v2.8 with additional gender and age controls.',
  'nansy-harmonic-token': 'Improved first-generation voice conversion model.',
  'nansy-harmonic-token-interpolation':
    'Improved first-generation model mode that blends multiple target voices during voice conversion.',
  'speaker-control': 'Legacy model specialised in changing gender and age.',
};

export const ParameterType = {
  Checkbox: 'checkbox',
  Slider: 'slider',
  Ratio: 'ratio',
} as const;
type Parameter = (typeof ParameterType)[keyof typeof ParameterType];
export interface CheckboxParameter {
  type?: Parameter;
  name: string;
  label: string;
  tooltip: string;
  defaultValue: boolean;
}
export interface SliderParameter
  extends Omit<CheckboxParameter, 'defaultValue'> {
  defaultValue: number;
  minValue: number;
  maxValue: number;
  step: number;
  minLabel?: string;
  maxLabel?: string;
  isToggleable?: boolean;
  defaultToggle?: boolean;
  valueFormatter?: (value: number) => string;
  disabled?: boolean;
}

const parameterMap: Record<string, CheckboxParameter | SliderParameter> = {
  keep_source_pitch: {
    type: ParameterType.Checkbox,
    name: 'keep_source_pitch',
    label: 'Keep Source Pitch',
    tooltip: '원본 소스 피치 복제',
    defaultValue: true,
  },

  keep_source_pitch_variance: {
    type: ParameterType.Checkbox,
    name: 'keep_source_pitch_variance',
    label: 'Keep Source Pitch Variance',
    tooltip: '원본 소스 음정 편차 복제',
    defaultValue: true,
  },

  semitone: {
    name: 'semitone',
    label: 'Pitch Shift',
    tooltip: '반음 단위로 수정',
    defaultValue: 0,
    minValue: -24,
    maxValue: 24,
    step: 1,
  },

  pitch_variance: {
    name: 'pitch_variance',
    label: 'Pitch Variance',
    tooltip: '음정 편차 조절',
    defaultValue: 1,
    minValue: 0,
    maxValue: 2,
    step: 0.1,
    minLabel: 'static',
    maxLabel: 'dynamic',
  },

  scale_factor: {
    name: 'scale_factor',
    label: 'Tempo Change',
    tooltip: '템포 조절',
    defaultValue: 1,
    minValue: 0.5,
    maxValue: 2,
    step: 0.1,
    minLabel: 'slow',
    maxLabel: 'fast',
  },

  temperature: {
    name: 'temperature',
    label: 'Target Similarity',
    tooltip: '타겟 유사도, 낮을수록 결과물이 랜덤하게 변합니다.',
    defaultValue: 2,
    minValue: 0,
    maxValue: 10,
    step: 1,
  },

  breathy_db: {
    name: 'breathy_db',
    label: 'Breath Control',
    tooltip: '호흡 조절, 높을수록 호흡이 많아집니다.',
    defaultValue: 0,
    minValue: 0,
    maxValue: 10,
    step: 1,
    isToggleable: false,
    defaultToggle: false,
  },

  distortion_db: {
    name: 'distortion_db',
    label: 'Clarity Control',
    tooltip: '명료도 조절',
    defaultValue: 0,
    minValue: 0,
    maxValue: 10,
    step: 1,
    isToggleable: false,
    defaultToggle: false,
  },

  whisper_ratio: {
    name: 'whisper_ratio',
    label: 'Whisper',
    tooltip: '속삭임 정도, 높을수록 더 속삭이는 것처럼 변합니다.',
    defaultValue: 0,
    minValue: 0,
    maxValue: 10,
    step: 1,
    isToggleable: false,
    defaultToggle: false,
  },

  tgt_gender: {
    name: 'tgt_gender',
    label: 'Target Gender',
    tooltip: '성별',
    defaultValue: 0,
    minValue: -15,
    maxValue: 15,
    step: 0.1,
    minLabel: 'F',
    maxLabel: 'M',
  },

  tgt_age: {
    name: 'tgt_age',
    label: 'Target Age',
    tooltip: '연령',
    defaultValue: 30,
    minValue: 0,
    maxValue: 100,
    step: 1,
  },

  norm_input_volume_db: {
    name: 'norm_input_volume_db',
    label: 'Input Volume (dB)',
    tooltip: '음량 조절',
    defaultValue: 0,
    minValue: -10,
    maxValue: 10,
    step: 0.1,
  },

  // nansy-v2.5
  speed: {
    name: 'speed',
    label: 'Tempo Change',
    tooltip: '템포 조절',
    defaultValue: 1,
    minValue: 0.5,
    maxValue: 2,
    step: 0.1,
    minLabel: 'slow',
    maxLabel: 'fast',
  },

  pitch_shift: {
    name: 'pitch_shift',
    label: 'Pitch Shift',
    tooltip: '반음 단위로 수정',
    defaultValue: 0,
    minValue: -24,
    maxValue: 24,
    step: 1,
  },

  pitch_dynamic: {
    name: 'pitch_dynamic',
    label: 'Pitch Variance',
    tooltip: '음정 편차 조절',
    defaultValue: 1,
    minValue: 0,
    maxValue: 2,
    step: 0.1,
    minLabel: 'static',
    maxLabel: 'dynamic',
  },

  keep_source_pitch_dynamic: {
    type: ParameterType.Checkbox,
    name: 'keep_source_pitch_dynamic',
    label: 'Keep Source Pitch Variance',
    tooltip: '원본 소스 음정 편차 복제',
    defaultValue: true,
  },

  segment: {
    type: ParameterType.Checkbox,
    name: 'segment',
    label: 'Segment',
    tooltip: 'Source 오디오를 유음 구간 단위로 나누어 처리할지 여부',
    defaultValue: false,
  },

  subharmonic_amplitude: {
    type: ParameterType.Slider,
    name: 'subharmonic_amplitude',
    label: 'Subharmonic Amplitude',
    tooltip: 'Subharmonic Amplitude',
    defaultValue: 0.0,
    minValue: 0.0,
    maxValue: 2.0,
    step: 0.1,
  },

  subharmonic_amplitude_v2_8: {
    type: ParameterType.Slider,
    name: 'subharmonic_amplitude',
    label: 'Subharmonic Amplitude',
    tooltip: 'Subharmonic Amplitude',
    defaultValue: 1.0,
    minValue: 0.0,
    maxValue: 2.0,
    step: 0.1,
  },

  keep_source_subharmonic_amplitude: {
    type: ParameterType.Checkbox,
    name: 'keep_source_subharmonic_amplitude',
    label: 'Keep Source Subharmonic Amp',
    tooltip: '원본 소스 subharmonic amplitude 복제',
    defaultValue: true,
  },

  // Add new parameters for nansy-v2.8-vdnet
  gender_age_mode: {
    type: ParameterType.Checkbox,
    name: 'gender_age_mode',
    label: 'Absolute Mode',
    tooltip:
      '성별과 나이를 절대값으로 설정합니다. 체크하지 않으면 상대적으로 조절합니다.',
    defaultValue: false,
  },

  // Parameters for relative mode
  age_shift: {
    name: 'age_shift',
    label: 'Age Shift',
    tooltip: '모델이 추정한 나이를 기준으로 상대적으로 나이를 조절합니다.',
    defaultValue: 0.0,
    minValue: -80,
    maxValue: 80,
    step: 1,
  },

  gender_shift: {
    name: 'gender_shift',
    label: 'Gender Shift',
    tooltip:
      '모델이 추정한 성별을 기준으로 상대적으로 성별을 조절합니다. (-1: 여성, 1: 남성)',
    defaultValue: 0.0,
    minValue: -2.0,
    maxValue: 2.0,
    step: 0.1,
    minLabel: 'F',
    maxLabel: 'M',
  },

  // Parameters for absolute mode
  age_trg: {
    name: 'age_trg',
    label: 'Target Age',
    tooltip: '절대적인 나이 값을 설정합니다.',
    defaultValue: 30,
    minValue: 0,
    maxValue: 80,
    step: 1,
  },

  gender_trg: {
    name: 'gender_trg',
    label: 'Target Gender',
    tooltip: '절대적인 성별 값을 설정합니다. (-1: 여성, 1: 남성)',
    defaultValue: 0,
    minValue: -1,
    maxValue: 1,
    step: 0.1,
    minLabel: 'F',
    maxLabel: 'M',
  },
};

const nansyV2Parameters = [
  parameterMap.keep_source_pitch,
  parameterMap.keep_source_pitch_variance,
  parameterMap.semitone,
  parameterMap.pitch_variance,
  parameterMap.scale_factor,
  parameterMap.temperature,
  parameterMap.breathy_db,
];
const speakerControlParameters = [
  parameterMap.tgt_age,
  parameterMap.tgt_gender,
  parameterMap.keep_source_pitch,
  parameterMap.semitone,
  parameterMap.pitch_variance,
  parameterMap.scale_factor,
  parameterMap.temperature,
  parameterMap.breathy_db,
  parameterMap.distortion_db,
  parameterMap.whisper_ratio,
];
const nansyHarmonicTokenParameters = [
  parameterMap.keep_source_pitch,
  parameterMap.semitone,
  parameterMap.pitch_variance,
  parameterMap.scale_factor,
  parameterMap.temperature,
  parameterMap.breathy_db,
  parameterMap.distortion_db,
  parameterMap.whisper_ratio,
];
const nansyHarmonicTokenInterpolationParameters = [
  parameterMap.keep_source_pitch,
  parameterMap.semitone,
  parameterMap.pitch_variance,
  parameterMap.scale_factor,
  parameterMap.temperature,
  parameterMap.breathy_db,
  parameterMap.distortion_db,
  parameterMap.whisper_ratio,
];
const nansyV2_5Parameters = [
  parameterMap.keep_source_pitch,
  parameterMap.keep_source_pitch_dynamic,
  parameterMap.segment,
  parameterMap.pitch_shift,
  parameterMap.pitch_dynamic,
  parameterMap.speed,
  parameterMap.subharmonic_amplitude,
];

const nansyV2_8Parameters = [
  parameterMap.keep_source_pitch,
  parameterMap.keep_source_pitch_dynamic,
  parameterMap.keep_source_subharmonic_amplitude,
  parameterMap.segment,
  parameterMap.pitch_shift,
  parameterMap.pitch_dynamic,
  parameterMap.speed,
  parameterMap.subharmonic_amplitude_v2_8,
];

// Define parameters for nansy-v2.8-vdnet
const nansyV2_8VDNetParameters = [
  parameterMap.keep_source_pitch,
  parameterMap.keep_source_pitch_dynamic,
  parameterMap.keep_source_subharmonic_amplitude,
  parameterMap.segment,
  parameterMap.pitch_shift,
  parameterMap.pitch_dynamic,
  parameterMap.speed,
  parameterMap.subharmonic_amplitude_v2_8,
  parameterMap.gender_age_mode,
  parameterMap.age_shift,
  parameterMap.gender_shift,
  parameterMap.age_trg,
  parameterMap.gender_trg,
];

export const modelParamMap: Record<ModelType, any> = {
  'nansy-v2': nansyV2Parameters,
  'nansy-v2-interpolation': nansyV2Parameters,
  'nansy-v2.5': nansyV2_5Parameters,
  'nansy-v2.5-interpolation': nansyV2_5Parameters,
  'nansy-v2.7': nansyV2_5Parameters,
  'nansy-v2.7-interpolation': nansyV2_5Parameters,
  'nansy-v2.8': nansyV2_8Parameters,
  'nansy-v2.8-vdnet': nansyV2_8VDNetParameters,
  'nansy-harmonic-token': nansyHarmonicTokenParameters,
  'speaker-control': speakerControlParameters,
  'nansy-harmonic-token-interpolation':
    nansyHarmonicTokenInterpolationParameters,
};
